import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

export type UserRole = 'user' | 'admin' | 'super_admin';

/**
 * Check if the current user is a super administrator
 *
 * Super admins have elevated permissions including:
 * - Editing global reference data (asset types, project types, etc.)
 * - Managing system-wide settings
 * - Accessing all organizations
 *
 * Checks the is_super_admin column directly on auth.users table via JWT claims
 *
 * @returns Query result with boolean indicating super admin status
 *
 * @example
 * ```tsx
 * const { data: isSuperAdmin } = useIsSuperAdmin();
 * if (isSuperAdmin) {
 *   // Show admin-only UI
 * }
 * ```
 */
export const useIsSuperAdmin = () => {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['isSuperAdmin', user?.id],
    queryFn: async (): Promise<boolean> => {
      if (!user?.id) return false;

      // Query auth.users table directly for is_super_admin column.
      const { data, error } = await supabase.rpc('is_current_user_super_admin');

      if (error) {
        // Fail closed. user_metadata is user-writable and must never be trusted
        // as a super-admin signal (see docs/USER_ROLES_AND_PERMISSIONS.md).
        console.error('Error checking super admin status:', error);
        return false;
      }

      return data === true;
    },
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });
};

/**
 * Get the current user's role
 *
 * Checks auth.users.is_super_admin column.
 * Note: Currently only supports 'user' and 'super_admin' roles.
 * The 'admin' role is reserved for future org-level admin functionality.
 *
 * @returns Query result with user role ('user', 'admin', 'super_admin')
 *
 * @example
 * ```tsx
 * const { data: userRole } = useUserRole();
 * ```
 */
export const useUserRole = () => {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['userRole', user?.id],
    queryFn: async (): Promise<UserRole | null> => {
      if (!user?.id) return null;

      // Check is_super_admin via RPC function
      const { data, error } = await supabase.rpc('is_current_user_super_admin');

      if (error) {
        // Fail closed — never derive super-admin from user-writable metadata.
        console.error('Error fetching user role:', error);
        return 'user';
      }

      if (data === true) return 'super_admin';

      // TODO: Add org-level admin check when implemented
      return 'user';
    },
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });
};

/**
 * Check if user has a specific permission/role
 *
 * @param requiredRole - Minimum role required
 * @returns Boolean indicating if user has required role or higher
 *
 * @example
 * ```tsx
 * const { data: canManageGlobal } = useHasRole('super_admin');
 * ```
 */
export const useHasRole = (requiredRole: UserRole) => {
  const { data: userRole } = useUserRole();

  const roleHierarchy: Record<UserRole, number> = {
    user: 1,
    admin: 2,
    super_admin: 3,
  };

  if (!userRole) return { data: false };

  return {
    data: roleHierarchy[userRole] >= roleHierarchy[requiredRole],
  };
};
